import { VerificationColumn, VerificationTable } from '../../../../../shared/components/VerificationTable';
import { parseNumericValue, thousandSeparator } from '../../../../../shared/utils/formatters.utils';

import type { Item } from '../../../types/item.types';
import React from 'react';
import { SubItem } from '../../../types/sub-item.types';
import { generatePartIdentifier } from '../utils/copy-paste.utils';
import { theme } from '../../../../../shared/styles/core/theme';

const roundedNumericValue = (value: unknown) => thousandSeparator(Math.round(parseNumericValue(value)));

const SOURCE_COLUMNS: VerificationColumn<SubItem>[] = [
    { header: 'Part detail', render: (s) => s.partDetail },
    { header: 'Part', render: (s) => s.part },
    { header: 'Material grade', render: (s) => s.materialGrade },
    { header: 'Material grade ID', render: (s) => s.materialGradeId },
    { header: 'Material set name', render: (s) => s.matSetName },
    { header: 'Composition share (%)', render: (s) => s.compositionShare ?? '-' },
    { header: '3D model no.', render: (s) => s.threeDModelNo },
    { header: 'Quantity', render: (s) => s.quantity ?? '-' },
    { header: 'Mass (g)', render: (s) => roundedNumericValue(s.mass) },
    { header: 'Volume (mm³)', render: (s) => roundedNumericValue(s.volume) },
    { header: 'Density (kg/m³)', render: (s) => roundedNumericValue(s.density) },
    { header: 'Recycle content (%)', render: (s) => s.recycledContent ?? '-' },
    { header: 'Renewable content (%)', render: (s) => s.renewableContent ?? '-' },
];

const TARGET_COLUMNS: VerificationColumn<Item>[] = [
    { header: 'Item number', render: (i) => i.itemNo },
    { header: 'HFB', render: (i) => i.hfb },
    { header: 'PA', render: (i) => i.pa },
    { header: 'Family name', render: (i) => i.familyName },
    { header: 'Item name', render: (i) => i.itemName },
];

interface SourcePartsTableProps {
    copiedParts: SubItem[];
    verifiedPartIds: string[];
    onChangeVerifiedPartIds: (ids: string[]) => void;
    isSubmitting: boolean;
}

export const SourcePartsTable = ({
    copiedParts,
    verifiedPartIds,
    onChangeVerifiedPartIds,
    isSubmitting,
}: SourcePartsTableProps) => (
    <VerificationTable
        label="Source parts"
        items={copiedParts}
        columns={SOURCE_COLUMNS}
        getItemId={generatePartIdentifier}
        selectedIds={verifiedPartIds}
        onChangeSelectedIds={onChangeVerifiedPartIds}
        isSubmitting={isSubmitting}
        getRowStyle={(s) => ({ color: s.active ? undefined : theme.colors.text.tertiary })}
    />
);

interface TargetItemsTableProps {
    resolvedSelectedItems: Item[];
    verifiedItemNos: string[];
    onChangeVerifiedItemNos: (itemNos: string[]) => void;
    isSubmitting: boolean;
}

export const TargetItemsTable = ({
    resolvedSelectedItems,
    verifiedItemNos,
    onChangeVerifiedItemNos,
    isSubmitting,
}: TargetItemsTableProps) => (
    <VerificationTable
        label="Target items"
        items={resolvedSelectedItems}
        columns={TARGET_COLUMNS}
        getItemId={(item) => item.itemNo}
        selectedIds={verifiedItemNos}
        onChangeSelectedIds={onChangeVerifiedItemNos}
        isSubmitting={isSubmitting}
    />
);
